import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';

/**
 * DataTab - Admin tab for managing system data configurations
 * Contains collapsible sections for different data types
 */
const DataTab = () => {
  const { instance, accounts } = useMsal();
  
  // Expanded sections state
  const [expandedSections, setExpandedSections] = useState({
    selectedContractors: false
  });
  
  // Contractor Disciplines state
  const [disciplines, setDisciplines] = useState([]);
  const [disciplinesLoading, setDisciplinesLoading] = useState(false);
  const [newDiscipline, setNewDiscipline] = useState('');
  const [editingDiscipline, setEditingDiscipline] = useState(null);
  const [editDisciplineValue, setEditDisciplineValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  
  // Fetch contractor disciplines
  const fetchDisciplines = useCallback(async () => {
    try {
      setDisciplinesLoading(true);
      setError(null);
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        '/contractors/disciplines'
      );
      if (response.data.success) {
        setDisciplines(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching disciplines:', err);
      setError('Failed to load disciplines');
    } finally {
      setDisciplinesLoading(false);
    }
  }, [instance, accounts]);
  
  // Load disciplines when section is expanded
  useEffect(() => {
    if (expandedSections.selectedContractors && disciplines.length === 0) {
      fetchDisciplines();
    }
  }, [expandedSections.selectedContractors, disciplines.length, fetchDisciplines]);
  
  // Add new discipline
  const handleAddDiscipline = async () => {
    if (!newDiscipline.trim()) return;
    
    try {
      setSaving(true);
      setError(null);
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        '/contractors/disciplines',
        { name: newDiscipline.trim() }
      );
      if (response.data.success) {
        setDisciplines(prev => [...prev, response.data.data]);
        setNewDiscipline('');
      }
    } catch (err) {
      console.error('Error adding discipline:', err);
      setError(err.response?.data?.message || 'Failed to add discipline');
    } finally {
      setSaving(false);
    }
  };
  
  // Update discipline
  const handleUpdateDiscipline = async (id) => {
    if (!editDisciplineValue.trim()) return;
    
    try {
      setSaving(true);
      setError(null);
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'PUT',
        `/contractors/disciplines/${id}`,
        { name: editDisciplineValue.trim() }
      );
      if (response.data.success) {
        setDisciplines(prev => prev.map(d => 
          d.id === id ? response.data.data : d
        ));
        setEditingDiscipline(null);
        setEditDisciplineValue('');
      }
    } catch (err) {
      console.error('Error updating discipline:', err);
      setError(err.response?.data?.message || 'Failed to update discipline');
    } finally {
      setSaving(false);
    }
  };
  
  // Delete discipline
  const handleDeleteDiscipline = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"? This cannot be undone.`)) {
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'DELETE',
        `/contractors/disciplines/${id}`
      );
      if (response.data.success) {
        setDisciplines(prev => prev.filter(d => d.id !== id));
      }
    } catch (err) {
      console.error('Error deleting discipline:', err);
      setError(err.response?.data?.message || 'Failed to delete discipline. It may be in use by contractors.');
    } finally {
      setSaving(false);
    }
  };
  
  // Start editing a discipline
  const startEditing = (discipline) => {
    setEditingDiscipline(discipline.id);
    setEditDisciplineValue(discipline.name);
  };
  
  // Cancel editing
  const cancelEditing = () => {
    setEditingDiscipline(null);
    setEditDisciplineValue('');
  };

  return (
    <div>
      <h3 style={{ marginBottom: '1rem' }}>Data Management</h3>
      <p style={{ color: '#666', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
        Manage system data configurations and lookup values.
      </p>
      
      {/* Error message */}
      {error && (
        <div style={{
          padding: '0.75rem 1rem',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          borderRadius: '4px',
          marginBottom: '1rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>{error}</span>
          <button 
            onClick={() => setError(null)}
            style={{ 
              background: 'none', 
              border: 'none', 
              cursor: 'pointer', 
              fontSize: '1.2rem',
              color: '#721c24'
            }}
          >
            ×
          </button>
        </div>
      )}
      
      {/* Selected Contractors Section */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '1rem',
        overflow: 'hidden'
      }}>
        {/* Section Header - Clickable */}
        <div 
          onClick={() => toggleSection('selectedContractors')}
          style={{
            padding: '1rem 1.5rem',
            backgroundColor: '#f8f9fa',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: expandedSections.selectedContractors ? '1px solid #e9ecef' : 'none'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span style={{ fontSize: '1.25rem' }}>🏢</span>
            <div>
              <h4 style={{ margin: 0, color: '#333' }}>Selected Contractors</h4>
              <p style={{ margin: '0.25rem 0 0 0', color: '#666', fontSize: '0.85rem' }}>
                Manage disciplines and other contractor data settings
              </p>
            </div>
          </div>
          <span style={{ 
            fontSize: '1.25rem', 
            transform: expandedSections.selectedContractors ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }}>
            ▼
          </span>
        </div>
        
        {/* Section Content - Collapsible */}
        {expandedSections.selectedContractors && (
          <div style={{ padding: '1.5rem' }}>
            {/* Disciplines Sub-section */}
            <div style={{
              border: '1px solid #e9ecef',
              borderRadius: '6px',
              padding: '1rem'
            }}>
              <h5 style={{ margin: '0 0 1rem 0', color: '#333', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                📋 Disciplines
                <span style={{ 
                  fontSize: '0.75rem', 
                  backgroundColor: '#e9ecef', 
                  padding: '0.125rem 0.5rem', 
                  borderRadius: '10px',
                  fontWeight: 'normal'
                }}>
                  {disciplines.length} total
                </span>
              </h5>
              <p style={{ color: '#666', marginBottom: '1rem', fontSize: '0.85rem' }}>
                Add or remove disciplines that can be assigned to contractors.
              </p>
              
              {/* Add new discipline form */}
              <div style={{ 
                display: 'flex', 
                gap: '0.5rem', 
                marginBottom: '1rem',
                padding: '0.75rem',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px'
              }}>
                <input
                  type="text"
                  value={newDiscipline}
                  onChange={(e) => setNewDiscipline(e.target.value)}
                  placeholder="Enter new discipline name..."
                  onKeyDown={(e) => e.key === 'Enter' && handleAddDiscipline()}
                  style={{
                    flex: 1,
                    padding: '0.5rem 0.75rem',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: '0.9rem'
                  }}
                  disabled={saving}
                />
                <button
                  onClick={handleAddDiscipline}
                  disabled={!newDiscipline.trim() || saving}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: newDiscipline.trim() && !saving ? '#28a745' : '#ccc',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: newDiscipline.trim() && !saving ? 'pointer' : 'not-allowed',
                    fontSize: '0.9rem',
                    fontWeight: '500'
                  }}
                >
                  {saving ? 'Adding...' : '+ Add'}
                </button>
              </div>
              
              {/* Disciplines list */}
              {disciplinesLoading ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                  Loading disciplines...
                </div>
              ) : disciplines.length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '2rem', 
                  color: '#666',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '4px'
                }}>
                  No disciplines defined yet. Add your first discipline above.
                </div>
              ) : (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '0.5rem',
                  maxHeight: '300px',
                  overflowY: 'auto'
                }}>
                  {disciplines.map(discipline => (
                    <div 
                      key={discipline.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0.5rem 0.75rem',
                        backgroundColor: '#fff',
                        border: '1px solid #e9ecef',
                        borderRadius: '4px'
                      }}
                    >
                      {editingDiscipline === discipline.id ? (
                        // Edit mode
                        <div style={{ display: 'flex', gap: '0.5rem', flex: 1 }}>
                          <input
                            type="text"
                            value={editDisciplineValue}
                            onChange={(e) => setEditDisciplineValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleUpdateDiscipline(discipline.id);
                              if (e.key === 'Escape') cancelEditing();
                            }}
                            style={{
                              flex: 1,
                              padding: '0.25rem 0.5rem',
                              border: '1px solid #007bff',
                              borderRadius: '3px',
                              fontSize: '0.9rem'
                            }}
                            autoFocus
                            disabled={saving}
                          />
                          <button
                            onClick={() => handleUpdateDiscipline(discipline.id)}
                            disabled={!editDisciplineValue.trim() || saving}
                            style={{
                              padding: '0.25rem 0.5rem',
                              backgroundColor: '#28a745',
                              color: 'white',
                              border: 'none',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              fontSize: '0.8rem'
                            }}
                          >
                            Save
                          </button>
                          <button
                            onClick={cancelEditing}
                            disabled={saving}
                            style={{
                              padding: '0.25rem 0.5rem',
                              backgroundColor: '#6c757d',
                              color: 'white',
                              border: 'none',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              fontSize: '0.8rem'
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        // View mode
                        <>
                          <span style={{ fontWeight: '500' }}>{discipline.name}</span>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            {discipline.usage_count > 0 && (
                              <span style={{
                                fontSize: '0.75rem',
                                color: '#666',
                                padding: '0.125rem 0.5rem',
                                backgroundColor: '#e9ecef',
                                borderRadius: '10px'
                              }}>
                                {discipline.usage_count} contractor{discipline.usage_count !== 1 ? 's' : ''}
                              </span>
                            )}
                            <button
                              onClick={() => startEditing(discipline)}
                              style={{
                                padding: '0.25rem 0.5rem',
                                backgroundColor: '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '3px',
                                cursor: 'pointer',
                                fontSize: '0.75rem'
                              }}
                              title="Edit discipline"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteDiscipline(discipline.id, discipline.name)}
                              disabled={discipline.usage_count > 0}
                              style={{
                                padding: '0.25rem 0.5rem',
                                backgroundColor: discipline.usage_count > 0 ? '#ccc' : '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '3px',
                                cursor: discipline.usage_count > 0 ? 'not-allowed' : 'pointer',
                                fontSize: '0.75rem'
                              }}
                              title={discipline.usage_count > 0 ? 'Cannot delete - in use by contractors' : 'Delete discipline'}
                            >
                              Delete
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Placeholder for future sub-sections */}
            <div style={{
              marginTop: '1rem',
              padding: '1rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '6px',
              border: '1px dashed #dee2e6',
              textAlign: 'center',
              color: '#999'
            }}>
              <p style={{ margin: 0, fontSize: '0.85rem' }}>
                More contractor data settings will be added here in the future
              </p>
            </div>
          </div>
        )}
      </div>
      
      {/* Placeholder for future data sections */}
      <WeldingProceduresSection instance={instance} accounts={accounts} />
    </div>
  );
};

/**
 * WeldingProceduresSection - Manage welding procedures (WPS) lookup table
 */
const WeldingProceduresSection = ({ instance, accounts }) => {
  const [expanded, setExpanded] = useState(false);
  const [procedures, setProcedures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newProcess, setNewProcess] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const fetchProcedures = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authenticatedApiCall(instance, accounts, 'GET', '/welding-procedures');
      if (response.data.success) {
        setProcedures(response.data.data || []);
      }
    } catch (err) {
      setError('Failed to load welding procedures');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts]);

  useEffect(() => {
    if (expanded && procedures.length === 0) {
      fetchProcedures();
    }
  }, [expanded, procedures.length, fetchProcedures]);

  const handleAdd = async () => {
    if (!newProcess.trim()) return;
    try {
      setSaving(true);
      setError(null);
      const response = await authenticatedApiCall(instance, accounts, 'POST', '/welding-procedures', { process: newProcess.trim() });
      if (response.data.success) {
        setProcedures(prev => [...prev, response.data.data]);
        setNewProcess('');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add procedure');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id) => {
    if (!editValue.trim()) return;
    try {
      setSaving(true);
      setError(null);
      const response = await authenticatedApiCall(instance, accounts, 'PUT', `/welding-procedures/${id}`, { process: editValue.trim() });
      if (response.data.success) {
        setProcedures(prev => prev.map(p => p.id === id ? response.data.data : p));
        setEditingId(null);
        setEditValue('');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update procedure');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      setSaving(true);
      const response = await authenticatedApiCall(instance, accounts, 'DELETE', `/welding-procedures/${id}`);
      if (response.data.success) {
        setProcedures(prev => prev.filter(p => p.id !== id));
      }
    } catch (err) {
      setError('Failed to delete procedure');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ border: '1px solid #dee2e6', borderRadius: '8px', overflow: 'hidden', marginTop: '1rem' }}>
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          width: '100%', padding: '1rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: expanded ? '#f8f9fa' : 'white', border: 'none', cursor: 'pointer', fontSize: '1rem', fontWeight: '600', color: '#333',
        }}
      >
        <span>🔧 Welding Procedures (WPS)</span>
        <span style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>▼</span>
      </button>

      {expanded && (
        <div style={{ padding: '1.25rem', borderTop: '1px solid #dee2e6' }}>
          {error && (
            <div style={{ padding: '0.5rem 1rem', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '4px', marginBottom: '1rem', fontSize: '0.85rem' }}>
              {error}
              <button onClick={() => setError(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>&times;</button>
            </div>
          )}

          {/* Add new */}
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
            <input
              value={newProcess}
              onChange={(e) => setNewProcess(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              placeholder="e.g. WPS-140"
              style={{ flex: 1, padding: '0.5rem', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '0.9rem' }}
            />
            <button
              onClick={handleAdd}
              disabled={!newProcess.trim() || saving}
              style={{ padding: '0.5rem 1rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '500' }}
            >
              Add
            </button>
          </div>

          {/* List */}
          {loading ? (
            <p style={{ color: '#666', fontSize: '0.85rem' }}>Loading...</p>
          ) : procedures.length === 0 ? (
            <p style={{ color: '#999', fontSize: '0.85rem' }}>No welding procedures defined yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              {procedures.map(proc => (
                <div key={proc.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0.75rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  {editingId === proc.id ? (
                    <>
                      <input
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleUpdate(proc.id); if (e.key === 'Escape') setEditingId(null); }}
                        style={{ flex: 1, padding: '0.25rem 0.5rem', border: '1px solid #80bdff', borderRadius: '3px', fontSize: '0.85rem' }}
                        autoFocus
                      />
                      <button onClick={() => handleUpdate(proc.id)} disabled={saving} style={{ padding: '0.2rem 0.5rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '0.75rem' }}>Save</button>
                      <button onClick={() => setEditingId(null)} style={{ padding: '0.2rem 0.5rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '0.75rem' }}>Cancel</button>
                    </>
                  ) : (
                    <>
                      <span style={{ flex: 1, fontSize: '0.9rem', fontWeight: '500' }}>{proc.process}</span>
                      <button onClick={() => { setEditingId(proc.id); setEditValue(proc.process); }} style={{ padding: '0.2rem 0.5rem', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '0.75rem' }}>Edit</button>
                      <button onClick={() => handleDelete(proc.id, proc.process)} style={{ padding: '0.2rem 0.5rem', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '0.75rem' }}>Delete</button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DataTab;
